/**
 * UI Preferences Manager
 *
 * This module provides centralized state management for UI preferences across all widget instances.
 * It uses React's useSyncExternalStore for efficient state synchronization.
 *
 * Key Features:
 * - Cross-widget UI preference synchronization (changing format in one widget affects all)
 * - Immutable state updates to ensure React detects changes
 * - Default American odds format
 *
 * Architecture:
 * 1. UIPreferencesManager - Singleton class managing all UI preference state
 * 2. useUIPreferences - React hook for components to subscribe to state changes
 * 3. Immutable Updates - All state changes create new object references for React
 */

import { useSyncExternalStore } from 'react'

type OddsFormat = 'american' | 'decimal'

interface UIPreferences {
  oddsFormat: OddsFormat
}

/**
 * Listener function type for React components subscribing to state changes
 */
type UIPreferencesListener = () => void

/**
 * UI Preferences Manager Class
 *
 * Singleton class that manages UI preference state across all widget instances.
 * Uses the Observer pattern with React's useSyncExternalStore for efficient updates.
 *
 * CRITICAL: All state updates must be immutable to ensure React detects changes.
 * This means creating new object references whenever the state changes.
 */
class UIPreferencesManager {
  private state: UIPreferences
  private listeners = new Set<UIPreferencesListener>()

  constructor() {
    this.state = { oddsFormat: 'american' }
  }

  /**
   * React's useSyncExternalStore integration
   * Subscribe method adds listeners that get called when state changes
   */
  subscribe = (listener: UIPreferencesListener) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * getSnapshot returns the current state for React's useSyncExternalStore
   * IMPORTANT: We return the state object reference directly. React compares
   * this reference using Object.is() to detect changes, which is why we use
   * immutable updates throughout this class.
   */
  getSnapshot = () => this.state

  /**
   * Notify all subscribed React components that state has changed
   * This triggers React to call getSnapshot() and re-render if needed
   */
  private notify = () => {
    this.listeners.forEach(listener => listener())
  }

  /**
   * Set odds format preference
   * Uses immutable updates to ensure React detects the change
   */
  setOddsFormat = (format: OddsFormat) => {
    // Create new state object to trigger React re-render
    this.state = { ...this.state, oddsFormat: format }
    this.notify()
  }

  /**
   * Toggle between american and decimal odds formats
   * Convenience method that determines the opposite format and sets it
   */
  toggleOddsFormat = () => {
    const newFormat = this.state.oddsFormat === 'american' ? 'decimal' : 'american'
    this.setOddsFormat(newFormat)
  }
}

/**
 * Singleton instance of the UIPreferencesManager
 * This single instance is shared across all widget instances and React components
 */
export const uiPreferencesManager = new UIPreferencesManager()

/**
 * React hook to use UI preferences state
 * Uses React's useSyncExternalStore for efficient subscription to state changes
 */
export function useUIPreferences() {
  const state = useSyncExternalStore(
    uiPreferencesManager.subscribe,
    uiPreferencesManager.getSnapshot,
  )

  return {
    oddsFormat: state.oddsFormat,
    setOddsFormat: uiPreferencesManager.setOddsFormat,
    toggleOddsFormat: uiPreferencesManager.toggleOddsFormat,
  }
}
